// ComponentsStrip — the scanned components (MCP servers, skills, agentic code)
// as selectable chips. Each shows finding + tool counts and a trust-tier colored
// underline; selecting one drives the findings + tools views below.
import { theme } from "antd";
import { tierToTone, type VMComponent, type Tone } from "@/lib/api/scanReport";

function toneColor(token: ReturnType<typeof theme.useToken>["token"], tone: Tone): string {
  switch (tone) {
    case "danger":
      return token.colorError;
    case "warning":
      return token.colorWarning;
    case "success":
      return token.colorSuccess;
    case "info":
      return token.colorInfo;
    default:
      return token.colorTextQuaternary;
  }
}

// A short display label: drop the redundant leading path noise for skills.
function label(c: VMComponent): string {
  if (c.type === "mcp-server") return c.serverName || c.name;
  return c.name;
}

export default function ComponentsStrip({
  components,
  selectedId,
  onSelect,
}: {
  components: VMComponent[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const { token } = theme.useToken();
  return (
    <div className="flex flex-wrap gap-2">
      {components.map((c) => {
        const selected = c.id === selectedId;
        const under = toneColor(token, tierToTone(c.trustTier ?? undefined));
        return (
          <button
            key={c.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(c.id)}
            title={`${c.type} · trust ${c.trustScore ?? "—"}/100 (${c.trustTier ?? "?"})`}
            className="group flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors"
            style={{
              background: selected ? token.colorFillSecondary : token.colorFillQuaternary,
              border: `1px solid ${selected ? token.colorBorder : "transparent"}`,
              borderBottom: `2px solid ${under}`,
            }}
          >
            <span
              className={`max-w-[220px] truncate ${selected ? "font-semibold text-text-primary" : "text-text-secondary"}`}
              style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 12 }}
            >
              {label(c)}
            </span>
            {/* Trust value as a number, not color alone (color-blind safety): low = worse. */}
            {c.trustScore != null && (
              <span
                className="rounded px-1 text-[11px] font-medium tabular-nums"
                style={{ background: token.colorFillQuaternary, color: under }}
                aria-label={`trust ${c.trustScore} of 100`}
              >
                {c.trustScore}
              </span>
            )}
            {c.findingsCount > 0 && (
              <span
                className="rounded px-1 text-[11px] tabular-nums"
                style={{ background: token.colorErrorBg, color: token.colorError }}
                aria-label={`${c.findingsCount} findings`}
              >
                {c.findingsCount}F
              </span>
            )}
            <span
              className="rounded px-1 text-[11px] tabular-nums"
              style={{ background: token.colorFillTertiary, color: token.colorTextSecondary }}
              aria-label={`${c.toolsCount} tools`}
            >
              {c.toolsCount}T
            </span>
          </button>
        );
      })}
    </div>
  );
}
